import { z } from "zod/v4";
import {
  IntervalUnit,
  ScheduleType,
  type CronSchedule,
  type IntervalSchedule,
  type VisualCronSchedule,
} from "../sync/scheduler/schedule-types";

/** Re-export sync schedule enums used by IDR create schedules. */
export { IntervalUnit, ScheduleType } from "../sync/scheduler/schedule-types";

/** Create-wizard schedule kinds not on sync `ScheduleType`. */
export const IdrCreateOnlyScheduleType = {
  Manual: "manual",
  Sequence: "sequence",
} as const;

export type IdrCreateOnlyScheduleType =
  (typeof IdrCreateOnlyScheduleType)[keyof typeof IdrCreateOnlyScheduleType];

/** Schedules IDR create / change plans may set. */
export type IdrCreateGraphSchedule =
  | { type: typeof IdrCreateOnlyScheduleType.Manual }
  | { type: ScheduleType.Interval; schedule: IntervalSchedule }
  | { type: ScheduleType.Cron; schedule: CronSchedule }
  | {
      type: ScheduleType.VisualCron;
      schedule: {
        expressions: Array<{
          time: string;
          days?: VisualCronSchedule["expressions"][number]["days"];
        }>;
      };
    }
  | { type: typeof IdrCreateOnlyScheduleType.Sequence };

const WeekdayFlagsSchema = z
  .object({
    monday: z.boolean().optional(),
    tuesday: z.boolean().optional(),
    wednesday: z.boolean().optional(),
    thursday: z.boolean().optional(),
    friday: z.boolean().optional(),
    saturday: z.boolean().optional(),
    sunday: z.boolean().optional(),
  })
  .strict();

/** Runtime validator for {@link IdrCreateGraphSchedule}. */
export const IdrCreateGraphScheduleSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(IdrCreateOnlyScheduleType.Manual) }),
  z.object({
    type: z.literal(ScheduleType.Interval),
    schedule: z.object({
      interval: z.object({
        quantity: z.number().int().positive(),
        unit: z.enum(IntervalUnit),
      }),
    }),
  }),
  z.object({
    type: z.literal(ScheduleType.Cron),
    schedule: z.object({
      expression: z.string().min(1),
    }),
  }),
  z.object({
    type: z.literal(ScheduleType.VisualCron),
    schedule: z.object({
      expressions: z
        .array(
          z.object({
            time: z.string().min(1),
            days: WeekdayFlagsSchema.optional(),
          }),
        )
        .min(1),
    }),
  }),
  z.object({ type: z.literal(IdrCreateOnlyScheduleType.Sequence) }),
]);

export function parseIdrCreateGraphSchedule(
  schedule: unknown,
): IdrCreateGraphSchedule {
  return IdrCreateGraphScheduleSchema.parse(schedule) as IdrCreateGraphSchedule;
}

export function isManualIdrCreateSchedule(
  schedule: IdrCreateGraphSchedule | null | undefined,
): boolean {
  return !schedule || schedule.type === IdrCreateOnlyScheduleType.Manual;
}
